/**
 * `/contacts/:id` — there is no contact page, and there should not be one.
 *
 * A contact hangs off **exactly one** of an account or a rep firm (§5.2), and
 * that parent is where their role, disposition and history make sense. The
 * command palette links here because `/api/search` returns contacts; this
 * forwards to the parent and highlights them.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiQuery, type Row } from '@/lib/queries';

export function ContactRedirect(): React.ReactElement {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const contact = useApiQuery<{ contact: Row }>(['contact', id], `/api/contacts/${id}`);

  useEffect(() => {
    const record = contact.data?.contact;
    if (record === undefined) return;
    const accountId = record['account_id'];
    const repFirmId = record['rep_firm_id'];
    if (typeof accountId === 'string')
      navigate(`/accounts/${accountId}?tab=contacts`, { replace: true });
    else if (typeof repFirmId === 'string') navigate(`/rep-firms/${repFirmId}`, { replace: true });
  }, [contact.data, navigate]);

  if (contact.error !== null) {
    return (
      <p className="rounded-md border border-destructive/40 bg-red-50 p-3 text-sm text-red-900">
        {contact.error.message}
      </p>
    );
  }

  return <p className="text-sm text-muted-foreground">Opening the account…</p>;
}
